if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        this.resolveCartResponse(formData, config)
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Tile products carry line-item properties (unit count, Total Sq.Ft/Linear.Ft,
      // Size, Lead Time, Item Type). Because the count + total vary per add, Shopify's
      // default merge would either split lines or stack quantity without summing the
      // totals. So for those adds we look for an existing matching line and reconcile
      // it ourselves; everything else (gift cards, etc.) adds normally.
      resolveCartResponse(formData, addConfig) {
        const properties = this.getLineProperties(formData);
        const feetKey = Object.keys(properties).find((key) => key.indexOf('Total ') === 0);

        if (!feetKey) {
          return fetch(`${routes.cart_add_url}`, addConfig).then((response) => response.json());
        }

        const variantId = Number(formData.get('id'));
        const addQuantity = parseInt(formData.get('quantity'), 10) || 1;

        return fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cart) => {
            const line = (cart.items || []).find(
              (item) => item.id === variantId && this.lineMatches(item.properties || {}, properties)
            );

            if (!line) {
              return fetch(`${routes.cart_add_url}`, addConfig).then((response) => response.json());
            }

            const newQuantity = line.quantity + addQuantity;
            const merged = this.mergeProperties(line.properties || {}, properties, newQuantity, feetKey);
            const changeConfig = this.buildChangeConfig(line.key, newQuantity, merged);
            return fetch(`${routes.cart_change_url}`, changeConfig).then((response) => response.json());
          })
          .catch(() => fetch(`${routes.cart_add_url}`, addConfig).then((response) => response.json()));
      }

      getLineProperties(formData) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }
        return properties;
      }

      // Same-variant lines merge when Size and pre-order status match. Lead Time is
      // deliberately excluded so the most recently added value wins on merge.
      lineMatches(existing, incoming) {
        return (
          (existing['Size'] || '') === (incoming['Size'] || '') &&
          (existing['Item Type'] || '') === (incoming['Item Type'] || '')
        );
      }

      mergeProperties(existing, incoming, newQuantity, feetKey) {
        const merged = { ...existing };

        // Lead Time: most recently added wins (self-heals a stale cart).
        if ('Lead Time' in incoming) merged['Lead Time'] = incoming['Lead Time'];

        // Total Sq.Ft / Linear.Ft: sum the two line totals.
        const existingFeet = parseFloat((existing[feetKey] || '0').toString().replace(/[^\d.-]/g, '')) || 0;
        const incomingFeet = parseFloat((incoming[feetKey] || '0').toString().replace(/[^\d.-]/g, '')) || 0;
        merged[feetKey] = (existingFeet + incomingFeet).toFixed(2);

        // Unit count (Boxes/Slabs/…) tracks the line quantity.
        Object.keys(incoming).forEach((key) => {
          if (key === 'Size' || key === 'Lead Time' || key === 'Item Type' || key.indexOf('Total ') === 0) return;
          merged[key] = String(newQuantity);
        });

        return merged;
      }

      buildChangeConfig(lineKey, quantity, properties) {
        const body = new FormData();
        body.append('id', String(lineKey));
        body.append('quantity', String(quantity));
        Object.keys(properties).forEach((key) => {
          body.append(`properties[${key}]`, properties[key]);
        });
        if (this.cart) {
          body.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          body.append('sections_url', window.location.pathname);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = body;
        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
